'use client'

import dynamic from 'next/dynamic'
import Link from 'next/link'
import { Box, Typography } from '@mui/material'

// Types
import type { StoryCardProps } from '@/types/components/molecules/stories'

// Components
const FramedImageAtom = dynamic(() => import('@/components/atoms/image/framed'))
const CountUpAtom = dynamic(() => import('@/components/atoms/animation/count-up'))
const ArrowRightIconAtom = dynamic(() => import('@/components/atoms/icon/bulk/arrow-right'))

// Gallery card for one success story: photo, business, the headline result and a link to the full story
const StoryCardMolecule = (props: StoryCardProps) => {
	// Props
	const { lng, brand, linkText } = props

	// Variables
	const { slug, name, person, personTitle, type, image, highlight, summary } = brand

	return (
		<Box
			component={Link}
			href={`/${lng}/success-stories/${slug}`}
			sx={{
				height: 1,
				display: 'flex',
				flexDirection: 'column',
				p: 1.5,
				borderRadius: 6,
				bgcolor: 'common.white',
				color: 'text.primary',
				textDecoration: 'none',
				transition: 'transform .35s cubic-bezier(0.22, 1, 0.36, 1), box-shadow .35s',
				'&:hover': { transform: 'translateY(-6px)', boxShadow: '0 24px 48px -20px rgba(23, 23, 23, 0.2)' },
				'&:hover .story-arrow': { translate: '4px 0' },
				'[dir=rtl] &:hover .story-arrow': { translate: '-4px 0' }
			}}
		>
			<Box sx={{ position: 'relative' }}>
				<FramedImageAtom src={image} alt={name} height={{ xs: 220, md: 240 }} radius={5} sizes="(max-width: 600px) 100vw, (max-width: 900px) 50vw, 380px" />

				<Box
					sx={{
						position: 'absolute',
						top: 12,
						insetInlineStart: 12,
						px: 1.5,
						py: 0.5,
						borderRadius: 999,
						bgcolor: 'rgba(255, 255, 255, 0.9)',
						fontSize: 12,
						fontWeight: 700
					}}
				>
					{type}
				</Box>
			</Box>

			<Box sx={{ px: 1.5, pt: 2.5, pb: 1.5, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
				<Typography component="h3" sx={{ fontSize: 19, fontWeight: 700 }}>
					{name}
				</Typography>

				<Typography sx={{ mt: 0.5, fontSize: 14, color: 'text.secondary' }}>{[person, personTitle].filter(Boolean).join('، ')}</Typography>

				<Box sx={{ mt: 2.5, fontSize: { xs: 30, md: 34 }, fontWeight: 800, lineHeight: 1.2, color: 'primary.main' }}>
					<CountUpAtom lng={lng} value={highlight.value} prefix={highlight.prefix} suffix={highlight.suffix} />
				</Box>

				<Typography sx={{ mt: 0.5, fontSize: 14, color: 'text.secondary' }}>{highlight.label}</Typography>

				<Typography sx={{ mt: 2, fontSize: 15, lineHeight: 1.9, color: 'text.secondary', flexGrow: 1 }}>{summary}</Typography>

				<Box sx={{ mt: 2.5, display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main', fontSize: 15, fontWeight: 600 }}>
					{linkText}

					<Box
						className="story-arrow"
						sx={{ display: 'flex', transition: 'translate .3s', '& .MuiSvgIcon-root': { fontSize: 20 }, '[dir=rtl] &': { transform: 'scaleX(-1)' } }}
					>
						<ArrowRightIconAtom />
					</Box>
				</Box>
			</Box>
		</Box>
	)
}

export default StoryCardMolecule
